// P2 — Modale création demande d'achat
import { useState, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { articlesApi, demandesAchatApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { Article } from "@/types/article";
import { API_BASE } from "@/lib/apiBase";
import { ht } from "@/lib/ht";

interface LigneDA {
  id: string; // local pour React keys
  article_id: number | null;
  designation: string;
  quantite: number;
  unite: string;
  prix_unitaire_fcfa: number;
  photos: string[]; // P20b — data URIs (max 2)
}

function makeEmptyLigne(): LigneDA {
  return {
    id: crypto.randomUUID(),
    article_id: null,
    designation: "",
    quantite: 1,
    unite: "pièce",
    prix_unitaire_fcfa: 0,
    photos: [],
  };
}

export default function CreateDAModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [objet, setObjet] = useState("");
  const [motif, setMotif] = useState("");
  const [type, setType] = useState<"approvisionnement" | "urgence">(
    "approvisionnement",
  );
  const [origine, setOrigine] = useState<"stock" | "manuel">("stock");
  const [lignes, setLignes] = useState<LigneDA[]>([makeEmptyLigne()]);

  // Charge articles pour autocomplete (si origine=stock)
  const { data: articles } = useQuery({
    queryKey: ["articles"],
    queryFn: () => articlesApi.list(),
    enabled: origine === "stock",
  });

  // Total live
  const total = useMemo(
    () =>
      lignes.reduce((acc, l) => acc + l.quantite * l.prix_unitaire_fcfa, 0),
    [lignes],
  );

  const createMutation = useMutation({
    mutationFn: () =>
      demandesAchatApi.create({
        objet,
        motif,
        type,
        lignes: lignes.map((l) => ({
          article_id: origine === "stock" ? l.article_id : null,
          designation: l.designation,
          quantite: l.quantite,
          unite: l.unite,
          prix_unitaire_fcfa: l.prix_unitaire_fcfa,
        })),
      }),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["demandes-achat"] });
      // P20b — Upload des photos sur chaque ligne créée
      const lignesWithPhotos = lignes.filter((l) => l.photos.length > 0);
      if (lignesWithPhotos.length > 0 && created.lignes) {
        Promise.all(
          created.lignes
            .map((ligne, idx) => {
              const original = lignes[idx];
              if (!original || original.photos.length === 0) return null;
              return fetch(
                `${API_BASE}/v1/demandes-achat/${created.id}/lignes/${ligne.id}/photos`,
                {
                  method: "PATCH",
                  credentials: "include",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ photos: original.photos }),
                },
              );
            })
            .filter(Boolean),
        ).catch((err) => console.warn("Photos upload warning:", err));
      }
      onClose();
      toast({ title: ht("DA créée"), description: created.code, variant: "success" });
    },
    onError: (err) => {
      toast({ title: ht("Erreur création DA"), description: (err as Error).message, variant: "error" });
    },
  });

  const updateLigne = (id: string, patch: Partial<LigneDA>) => {
    setLignes((prev) =>
      prev.map((l) => (l.id === id ? { ...l, ...patch } : l)),
    );
  };

  const removeLigne = (id: string) => {
    setLignes((prev) => prev.filter((l) => l.id !== id));
  };

  const selectArticle = (ligneId: string, article: Article) => {
    updateLigne(ligneId, {
      article_id: article.id,
      designation: article.designation,
      unite: article.unite,
      prix_unitaire_fcfa: article.prix_unitaire_fcfa,
    });
  };

  const canSubmit =
    objet.trim().length > 0 &&
    motif.trim().length > 0 &&
    lignes.length > 0 &&
    lignes.every((l) => l.designation.length > 0 && l.quantite > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-[min(48rem,calc(100vw-2rem))] max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>{ht("Nouvelle demande d'achat")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* En-tête */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="text-xs">{ht("Objet *")}</label>
                <input
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={objet}
                  onChange={(e) => setObjet(e.target.value)}
                  placeholder={ht("Ex: Pièces détachées intervention #42")}
                />
              </div>
              <div>
                <label className="text-xs">{ht("Type")}</label>
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={type}
                  onChange={(e) =>
                    setType(e.target.value as "approvisionnement" | "urgence")
                  }
                >
                  <option value="approvisionnement">{ht("Approvisionnement")}</option>
                  <option value="urgence">{ht("Urgence")}</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs">{ht("Motif *")}</label>
              <textarea
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                rows={2}
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                placeholder={ht("Justification de la demande")}
              />
            </div>

            {/* Origine */}
            <div>
              <label className="text-xs">{ht("Origine")}</label>
              <div className="mt-1 flex gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="origine"
                    checked={origine === "stock"}
                    onChange={() => setOrigine("stock")}
                  />
                  Depuis stock (sélection article)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="origine"
                    checked={origine === "manuel"}
                    onChange={() => setOrigine("manuel")}
                  />
                  Manuel (désignation libre)
                </label>
              </div>
            </div>

            {/* Lignes */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="text-xs font-semibold">
                  Lignes ({lignes.length})
                </label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setLignes([...lignes, makeEmptyLigne()])}
                >
                  + Ligne
                </Button>
              </div>

              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                    {origine === "stock" && (
                      <th className="px-2 py-1">Article</th>
                    )}
                    <th className="px-2 py-1">{ht("Désignation")}</th>
                    <th className="px-2 py-1 w-20">{ht("Qté")}</th>
                    <th className="px-2 py-1 w-20">{ht("Unité")}</th>
                    <th className="px-2 py-1 w-24">{ht("P.U. FCFA")}</th>
                    <th className="px-2 py-1 w-24">{ht("Total")}</th>
                    <th className="px-2 py-1 w-32">{ht("Photos (max 2)")}</th>
                    <th className="px-2 py-1 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((l) => (
                    <tr key={l.id} className="border-b">
                      {origine === "stock" && (
                        <td className="px-2 py-1">
                          <select
                            className="w-full rounded border border-input bg-background px-2 py-1 text-xs"
                            value={l.article_id ?? ""}
                            onChange={(e) => {
                              const art = articles?.find(
                                (a) => a.id === Number(e.target.value),
                              );
                              if (art) selectArticle(l.id, art);
                            }}
                          >
                            <option value="">— Choisir —</option>
                            {articles?.map((a) => (
                              <option key={a.id} value={a.id}>
                                {a.reference} ({a.unite})
                              </option>
                            ))}
                          </select>
                        </td>
                      )}
                      <td className="px-2 py-1">
                        <input
                          className="w-full rounded border border-input bg-background px-2 py-1 text-sm"
                          value={l.designation}
                          onChange={(e) =>
                            updateLigne(l.id, { designation: e.target.value })
                          }
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="number"
                          min="0"
                          className="w-full rounded border border-input bg-background px-2 py-1 text-sm"
                          value={l.quantite}
                          onChange={(e) =>
                            updateLigne(l.id, {
                              quantite: Number(e.target.value),
                            })
                          }
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          className="w-full rounded border border-input bg-background px-2 py-1 text-sm"
                          value={l.unite}
                          onChange={(e) =>
                            updateLigne(l.id, { unite: e.target.value })
                          }
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="number"
                          min="0"
                          className="w-full rounded border border-input bg-background px-2 py-1 text-sm"
                          value={l.prix_unitaire_fcfa}
                          onChange={(e) =>
                            updateLigne(l.id, {
                              prix_unitaire_fcfa: Number(e.target.value),
                            })
                          }
                        />
                      </td>
                      <td className="px-2 py-1 text-right font-mono">
                        {(l.quantite * l.prix_unitaire_fcfa).toLocaleString(
                          "fr-FR",
                        )}
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          disabled={l.photos.length >= 2}
                          onChange={async (e) => {
                            const files = Array.from(e.target.files ?? []).slice(0, 2 - l.photos.length);
                            const newPhotos: string[] = [];
                            for (const file of files) {
                              if (file.size > 5 * 1024 * 1024) {
                                toast({ title: ht("Photo > 5MB ignorée"), description: file.name, variant: "warning" });
                                continue;
                              }
                              const dataUri = await new Promise<string>((resolve) => {
                                const reader = new FileReader();
                                reader.onload = () => resolve(reader.result as string);
                                reader.readAsDataURL(file);
                              });
                              newPhotos.push(dataUri);
                            }
                            updateLigne(l.id, { photos: [...l.photos, ...newPhotos].slice(0, 2) });
                            e.target.value = ""; // reset input
                          }}
                          className="w-full text-xs"
                        />
                        {l.photos.length > 0 && (
                          <div className="mt-1 flex gap-1">
                            {l.photos.map((p, i) => (
                              <div key={i} className="relative">
                                <img src={p} alt={`Photo ${i + 1}`} className="h-8 w-8 rounded object-cover" />
                                <button
                                  type="button"
                                  onClick={() => updateLigne(l.id, { photos: l.photos.filter((_, idx) => idx !== i) })}
                                  className="absolute -right-1 -top-1 rounded-full bg-destructive px-1 text-xs text-white"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1">
                        <button
                          type="button"
                          className="text-red-600 text-xs"
                          onClick={() => removeLigne(l.id)}
                          disabled={lignes.length === 1}
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t font-semibold">
                    <td
                      colSpan={origine === "stock" ? 5 : 4}
                      className="px-2 py-2 text-right"
                    >{ht("Total")}
                    </td>
                    <td className="px-2 py-2 text-right font-mono">
                      {total.toLocaleString("fr-FR")} FCFA
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {createMutation.error && (
              <div className="rounded bg-red-50 p-3 text-sm text-red-700">
                {(createMutation.error as Error).message}
              </div>
            )}
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>{ht("Annuler")}
            </Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!canSubmit || createMutation.isPending}
            >
              {createMutation.isPending ? "Création..." : "Créer DA"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
